import React from "react";
import { useParams } from "react-router-dom";
import PublicListingV2 from "@/components/publicListing/PublicListingV2";

/**
 * AnteprimaVetrina — pagina nascosta usata SOLO per generare gli
 * screenshot dei 4 layout della Vetrina online per il mockup landing.
 *
 * URL: /anteprima/vetrina/:style  (es. /anteprima/vetrina/moderno)
 *
 * iter v28 r241 — User M1158/M1159: 4 annunci finti realistici, uno per
 * stile, così la landing mostra contenuti vari (Bilocale Brera, Attico
 * Navigli, Villa Brianza, Loft Isola) anziché lo stesso immobile 4 volte.
 * NIENTE link in menu: solo per Playwright screenshot capture.
 */

const COMMON_AGENCY = {
  name: "Verde Immobiliare",
  agency_name: "Verde Immobiliare",
  agent_name: "Luca Verdi",
  agent_phone: "+39 02 8400 1234",
  agent_email: "luca@verdeimmobiliare.it",
  web: "verdeimmobiliare.it",
  address: "Corso Garibaldi 88, Milano",
};

// iter v28 r242 — User M1160: sala-after = hero in alta definizione.
// Le altre 4 (cucina/camera/bagno/esterno) compongono la galleria sotto.
// PublicListingV2 mostra: photos[0] come prima slide hero + tutte come thumb
// galleria. Quindi metto sala-after IN PRIMA posizione e poi 3-4 secondarie.
const HERO_LIVING = { url: "/landing/sala-after.jpg", ai_used: false, is_cover: true };
const GALLERY_EXTRA = [
  { url: "/landing/cucina-after.jpg", ai_used: false, label: "Cucina" },
  { url: "/landing/camera-after.jpg", ai_used: false, label: "Camera" },
  { url: "/landing/bagno-after.jpg", ai_used: false, label: "Bagno" },
  { url: "/landing/esterno-after.jpg", ai_used: false, label: "Esterno" },
];

const PHOTOS_ALL = [HERO_LIVING, ...GALLERY_EXTRA];

const STYLES = {
  elegante: {
    publicId: "demo-anteprima-elegante",
    p: {
      public_id: "demo-anteprima-elegante",
      title: "Bilocale luminoso · Brera",
      subtitle: "Doppia esposizione · parquet originale",
      city: "Milano",
      address: "Via Solferino 18, 20121 Milano",
      property_type: "Bilocale",
      rooms: 2,
      bathrooms: 1,
      surface_area: 72,
      floor: "3° piano",
      price: 480000,
      energy_class: "B",
      ai_description:
        "Bilocale completamente ristrutturato a due passi da Brera, terzo piano con ascensore. Doppia esposizione, ampie finestre sulla via tranquilla, parquet a spina ungherese restaurato. Cucina abitabile open su living, camera matrimoniale, bagno con vasca. Climatizzazione, infissi doppi vetro, cantina al piano interrato. Pronto da abitare.",
      operation_type: "vendita",
      is_affitto: false,
      features: ["Ristrutturato", "Ascensore", "Doppia esposizione", "Parquet originale"],
    },
    photos: PHOTOS_ALL,
    agency: { ...COMMON_AGENCY, brand_color: "#A8B59A" },
  },
  moderno: {
    publicId: "demo-anteprima-moderno",
    p: {
      public_id: "demo-anteprima-moderno",
      title: "Attico vista Navigli · Porta Genova",
      subtitle: "Sesto piano · terrazzo abitabile",
      city: "Milano",
      address: "Via Gola 24, 20143 Milano",
      property_type: "Attico",
      rooms: 4,
      bathrooms: 2,
      surface_area: 145,
      floor: "6° piano",
      price: 895000,
      energy_class: "A",
      ai_description:
        "Attico panoramico con vista sui Navigli, sesto piano servito da ascensore. Ampio living open con camino centrale, cucina abitabile, tre camere, due bagni. Terrazzo abitabile di 35 m² ideale per cene estive. Materiali di pregio: parquet rovere chiaro, gres effetto pietra, finiture in ottone spazzolato.",
      operation_type: "vendita",
      is_affitto: false,
      features: ["Terrazzo abitabile", "Vista canale", "Climatizzato", "Cantina", "Box auto"],
    },
    photos: PHOTOS_ALL,
    agency: { ...COMMON_AGENCY, brand_color: "#D47A54" },
  },
  lussuoso: {
    publicId: "demo-anteprima-lussuoso",
    p: {
      public_id: "demo-anteprima-lussuoso",
      title: "Villa storica con parco · Brianza",
      subtitle: "240 m² · giardino 800 m² · piscina riscaldata",
      city: "Monza",
      address: "Via dei Tigli 7, 20900 Monza",
      property_type: "Villa",
      rooms: 6,
      bathrooms: 3,
      surface_area: 240,
      floor: "—",
      price_hidden: true,
      hide_price: true,
      price: null,
      energy_class: "A",
      ai_description:
        "Villa storica di pregio in posizione riservata della Brianza, completamente ristrutturata con cura sartoriale. Pianterreno con doppio salone, sala da pranzo, cucina padronale, sala di rappresentanza. Primo piano con 4 camere padronali, 3 bagni di servizio, studio. Parco di 800 m² con piscina riscaldata, dependance, garage doppio.",
      operation_type: "vendita",
      is_affitto: false,
      features: ["Parco privato", "Piscina riscaldata", "Sala camino", "Garage doppio", "Cantina vini"],
    },
    photos: PHOTOS_ALL,
    agency: { ...COMMON_AGENCY, brand_color: "#2B3654" },
  },
  minimal: {
    publicId: "demo-anteprima-minimal",
    p: {
      public_id: "demo-anteprima-minimal",
      title: "Loft restaurato · Isola",
      subtitle: "Open space industriale · luce naturale",
      city: "Milano",
      address: "Via Borsieri 32, 20159 Milano",
      property_type: "Loft",
      rooms: 3,
      bathrooms: 1,
      surface_area: 95,
      floor: "2° piano",
      price: 565000,
      energy_class: "B",
      ai_description:
        "Loft industrial-chic nel cuore di Isola, ex spazio artigianale convertito in residenza. Open space living-cucina-zona pranzo, due camere matrimoniali, bagno con doccia camminata. Travi a vista, parquet recuperato, finestre con cornice in ferro nero. Domotica integrata, climatizzazione, cantina e ascensore di servizio.",
      operation_type: "vendita",
      is_affitto: false,
      features: ["Travi a vista", "Open space", "Domotica", "Ascensore", "Cantina"],
    },
    photos: PHOTOS_ALL,
    agency: { ...COMMON_AGENCY, brand_color: "#FFFFFF" },
  },
};

export default function AnteprimaVetrina() {
  const { style } = useParams();
  const effective = (style || "elegante").toLowerCase();
  const DATA = STYLES[effective] || STYLES.elegante;
  return (
    <PublicListingV2
      data={{
        publicId: DATA.publicId,
        p: DATA.p,
        photos: DATA.photos,
        agency: DATA.agency,
      }}
      style={effective}
    />
  );
}
